import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import {
  Megaphone, Plus, PaperPlaneTilt, CheckCircle, XCircle, ClockCounterClockwise,
  ChartBar, Users, FileText, Funnel, Trash, PencilSimple, PaperclipHorizontal,
  CurrencyInr, Warning, TrendUp, ListChecks,
} from "@phosphor-icons/react";
import { getStatusBadgeClass } from "../config/uiHelpers";

const TAB_KEYS = ["dashboard", "campaigns", "approvals", "leads", "reports", "alerts"];

const EMPTY_CAMPAIGN = {
  name: "", type: "lead_generation", objective: "", product: "",
  start_date: "", end_date: "", department: "", region: "",
  description: "", expected_leads: 0, expected_revenue: 0,
  budget_planned: 0, channels: [], audience_segments: [],
  source_code: "", utm: "", notes: "",
};

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const StageBadge = ({ stage }) => {
  const map = {
    new: "bg-slate-200 text-slate-700",
    contacted: "bg-blue-100 text-blue-700",
    interested: "bg-indigo-100 text-indigo-700",
    follow_up: "bg-amber-100 text-amber-700",
    pending: "bg-yellow-100 text-yellow-700",
    mql: "bg-purple-100 text-purple-700",
    sql: "bg-fuchsia-100 text-fuchsia-700",
    opportunity: "bg-cyan-100 text-cyan-700",
    won: "bg-emerald-100 text-emerald-700",
    lost: "bg-red-100 text-red-700",
    disqualified: "bg-slate-300 text-slate-700",
  };
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded ${map[stage] || "bg-slate-200"}`}>{(stage || "").replace(/_/g, " ")}</span>;
};

const MarketingCampaigns = ({ user, logout }) => {
  const [tab, setTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [config, setConfig] = useState({ types: [], channels: [], statuses: [], lead_stages: [] });
  const [dash, setDash] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [users, setUsers] = useState([]);

  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null); // null | {campaign_id, ...}
  const [form, setForm] = useState(EMPTY_CAMPAIGN);

  const [detail, setDetail] = useState(null); // selected campaign id for detail drawer
  const [detailData, setDetailData] = useState(null); // {campaign, metrics, leads, approvals, docs, comms, spends}

  const [allLeads, setAllLeads] = useState([]);
  const [reportType, setReportType] = useState("campaign_roi");
  const [reportRows, setReportRows] = useState([]);

  const role = user?.role || "customer";
  const canCreate = ["admin", "marketing_head", "manager", "bdm"].includes(role);
  const canApprove = ["admin", "marketing_head", "manager", "cfo"].includes(role);
  const canDelete = ["admin", "marketing_head"].includes(role);

  const loadCore = useCallback(async () => {
    try {
      setLoading(true);
      const [c, cfg, d, a, u] = await Promise.all([
        axios.get(`${API}/mktg-campaigns?requester_role=${role}&requester_id=${user?.user_id || ""}`),
        axios.get(`${API}/mktg-campaigns-config`),
        axios.get(`${API}/mktg-campaigns-dashboard`),
        axios.get(`${API}/mktg-campaigns-alerts`),
        api.get(`/users`).catch(() => ({ data: [] })),
      ]);
      setCampaigns(c.data);
      setConfig(cfg.data);
      setDash(d.data);
      setAlerts(a.data);
      setUsers(u.data.filter(x => x.role !== "customer"));
    } catch (e) {
      toast.error("Failed to load marketing data");
    } finally {
      setLoading(false);
    }
  }, [role, user?.user_id]);

  useEffect(() => { loadCore(); }, [loadCore]);

  useEffect(() => {
    if (tab === "leads") {
      axios.get(`${API}/mktg-campaign-leads?requester_role=${role}&requester_id=${user?.user_id || ""}`)
        .then(r => setAllLeads(r.data)).catch(() => setAllLeads([]));
    }
  }, [tab, role, user?.user_id]);

  useEffect(() => {
    if (tab === "reports") runReport(reportType);
  }, [tab, reportType]); // eslint-disable-line react-hooks/exhaustive-deps

  const runReport = async (t) => {
    try {
      const r = await axios.get(`${API}/mktg-campaigns-reports/${t}`);
      setReportRows(r.data.rows || []);
    } catch { setReportRows([]); }
  };

  const openCreate = () => {
    setForm(EMPTY_CAMPAIGN);
    setEditing(null);
    setShowCreate(true);
  };

  const openEdit = (c) => {
    setForm({
      name: c.name || "", type: c.type || "lead_generation", objective: c.objective || "",
      product: c.product || "", start_date: c.start_date || "", end_date: c.end_date || "",
      department: c.department || "", region: c.region || "",
      description: c.description || "",
      expected_leads: c.expected_leads || 0, expected_revenue: c.expected_revenue || 0,
      budget_planned: c.budget_planned || 0, channels: c.channels || [],
      audience_segments: c.audience_segments || [],
      source_code: c.source_code || "", utm: c.utm || "", notes: c.notes || "",
      owner_id: c.owner_id || "", owner_name: c.owner_name || "",
    });
    setEditing(c);
    setShowCreate(true);
  };

  const saveCampaign = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/mktg-campaigns/${editing.campaign_id}`, form);
        toast.success("Campaign updated");
      } else {
        await axios.post(`${API}/mktg-campaigns`, {
          ...form,
          owner_id: form.owner_id || user.user_id,
          owner_name: form.owner_name || user.name,
          created_by_id: user.user_id, created_by_name: user.name,
        });
        toast.success("Campaign created");
      }
      setShowCreate(false);
      loadCore();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const deleteCampaign = async (c) => {
    if (!window.confirm(`Delete campaign ${c.campaign_code}?`)) return;
    try {
      await axios.delete(`${API}/mktg-campaigns/${c.campaign_id}?requester_role=${role}`);
      toast.success("Deleted");
      loadCore();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const submitForApproval = async (c) => {
    try {
      await axios.post(`${API}/mktg-campaigns/${c.campaign_id}/submit`, {
        by_user_id: user.user_id, by_user_name: user.name, by_role: role,
      });
      toast.success("Submitted for approval");
      loadCore();
      if (detail === c.campaign_id) openDetail(c.campaign_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
  };

  const approveCampaign = async (c, remarks = "") => {
    try {
      const r = await axios.post(`${API}/mktg-campaigns/${c.campaign_id}/approve`, {
        by_user_id: user.user_id, by_user_name: user.name, by_role: role, remarks,
      });
      toast.success(`Advanced to ${r.data.status.replace(/_/g, " ")}`);
      loadCore();
      if (detail === c.campaign_id) openDetail(c.campaign_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };

  const rejectCampaign = async (c) => {
    const remarks = window.prompt("Rejection reason:", "");
    if (remarks === null) return;
    try {
      await axios.post(`${API}/mktg-campaigns/${c.campaign_id}/reject`, {
        by_user_id: user.user_id, by_user_name: user.name, by_role: role, remarks,
      });
      toast.success("Rejected — returned to draft");
      loadCore();
      if (detail === c.campaign_id) openDetail(c.campaign_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
  };

  const setManualStatus = async (c, newStatus) => {
    try {
      await axios.post(`${API}/mktg-campaigns/${c.campaign_id}/status`, {
        status: newStatus, by_user_id: user.user_id, by_user_name: user.name, by_role: role,
      });
      toast.success(`Status: ${newStatus}`);
      loadCore();
      if (detail === c.campaign_id) openDetail(c.campaign_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const openDetail = async (campaignId) => {
    setDetail(campaignId);
    try {
      const [perf, approvals, leads, docs, comms, spends] = await Promise.all([
        axios.get(`${API}/mktg-campaigns/${campaignId}/performance`),
        axios.get(`${API}/mktg-campaigns/${campaignId}/approvals`),
        axios.get(`${API}/mktg-campaigns/${campaignId}/leads`),
        axios.get(`${API}/mktg-campaigns/${campaignId}/docs`),
        axios.get(`${API}/mktg-campaigns/${campaignId}/comms`),
        axios.get(`${API}/mktg-campaigns/${campaignId}/spend`),
      ]);
      setDetailData({
        ...perf.data,
        approvals: approvals.data,
        leads: leads.data,
        docs: docs.data,
        comms: comms.data,
        spends: spends.data,
      });
    } catch {
      toast.error("Failed to load campaign detail");
    }
  };

  const closeDetail = () => { setDetail(null); setDetailData(null); };

  const pendingApprovals = useMemo(() => {
    return campaigns.filter(c => ["submitted", "manager_approved", "marketing_head_approved"].includes(c.status));
  }, [campaigns]);

  const toggleChannel = (ch) => {
    setForm(f => ({ ...f, channels: f.channels.includes(ch) ? f.channels.filter(x => x !== ch) : [...f.channels, ch] }));
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50"><Sidebar user={user} logout={logout} /><div className="ml-64 p-12">Loading…</div></div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="mktg-campaigns-page">
      <Sidebar user={user} logout={logout} />
      <div className="ml-64">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Megaphone size={28} weight="duotone" className="text-[#002FA7]" />
              <div>
                <h1 className="text-xl font-bold text-slate-900">Marketing Campaigns</h1>
                <p className="text-xs text-slate-500">Plan · Execute · Convert · Measure</p>
              </div>
            </div>
            {canCreate && (
              <button data-testid="mktg-new-campaign-btn" onClick={openCreate}
                className="flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white rounded hover:bg-[#001f7a] text-sm font-medium">
                <Plus size={16} /> New Campaign
              </button>
            )}
          </div>
          {/* Tabs */}
          <div className="px-6 border-t border-slate-100 flex items-center gap-1 overflow-x-auto">
            {TAB_KEYS.map(k => (
              <button key={k} data-testid={`mktg-tab-${k}`} onClick={() => setTab(k)}
                className={`px-4 py-2 text-sm font-semibold border-b-2 capitalize ${tab === k ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                {k}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {tab === "dashboard" && dash && (
            <DashboardView dash={dash} alerts={alerts} onOpenCampaign={openDetail} />
          )}
          {tab === "campaigns" && (
            <CampaignsList campaigns={campaigns} role={role} canDelete={canDelete}
              onOpen={openDetail} onEdit={openEdit} onSubmit={submitForApproval}
              onDelete={deleteCampaign} onSetStatus={setManualStatus} />
          )}
          {tab === "approvals" && (
            <ApprovalsList campaigns={pendingApprovals} role={role} canApprove={canApprove}
              onOpen={openDetail} onApprove={approveCampaign} onReject={rejectCampaign} />
          )}
          {tab === "leads" && (
            <LeadsTable leads={allLeads} users={users} reload={() => {
              axios.get(`${API}/mktg-campaign-leads?requester_role=${role}&requester_id=${user?.user_id || ""}`)
                .then(r => setAllLeads(r.data));
            }} stages={config.lead_stages} />
          )}
          {tab === "reports" && (
            <ReportsView reportType={reportType} setReportType={setReportType} rows={reportRows} />
          )}
          {tab === "alerts" && alerts && (
            <AlertsView alerts={alerts} />
          )}
        </div>
      </div>

      {/* Create / Edit modal */}
      {showCreate && (
        <CampaignEditorModal
          form={form} setForm={setForm}
          editing={editing}
          config={config}
          users={users}
          onClose={() => setShowCreate(false)}
          onSave={saveCampaign}
          toggleChannel={toggleChannel}
        />
      )}

      {/* Detail drawer */}
      {detail && detailData && (
        <CampaignDetailDrawer
          data={detailData}
          role={role}
          canApprove={canApprove}
          canDelete={canDelete}
          onClose={closeDetail}
          onSubmit={submitForApproval}
          onApprove={approveCampaign}
          onReject={rejectCampaign}
          onSetStatus={setManualStatus}
          onDelete={deleteCampaign}
          onEdit={openEdit}
          reload={() => openDetail(detail)}
          user={user}
          config={config}
        />
      )}
    </div>
  );
};

/* ------------- Dashboard tab ------------- */
const KpiTile = ({ label, value, icon: Icon, accent = "#002FA7", testId }) => (
  <div className="bg-white p-4 rounded border border-slate-200" data-testid={testId}>
    <div className="flex items-center justify-between mb-1">
      <p className="text-xs uppercase font-bold text-slate-500 tracking-wider">{label}</p>
      <Icon size={18} weight="duotone" style={{ color: accent }} />
    </div>
    <p className="text-2xl font-bold text-slate-900">{value}</p>
  </div>
);

const DashboardView = ({ dash, alerts, onOpenCampaign }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <KpiTile label="Total Campaigns" value={dash.total_campaigns} icon={Megaphone} testId="kpi-total-campaigns" />
      <KpiTile label="Active" value={dash.active_campaigns} icon={CheckCircle} accent="#10b981" testId="kpi-active" />
      <KpiTile label="Total Leads" value={dash.total_leads} icon={Users} accent="#8b5cf6" testId="kpi-leads" />
      <KpiTile label="MQL" value={dash.total_mql} icon={Funnel} accent="#f59e0b" testId="kpi-mql" />
      <KpiTile label="SQL" value={dash.total_sql} icon={ListChecks} accent="#ef4444" testId="kpi-sql" />
      <KpiTile label="Won" value={dash.total_won} icon={TrendUp} accent="#10b981" testId="kpi-won" />
      <KpiTile label="Revenue Closed" value={fmtINR(dash.total_revenue_closed)} icon={CurrencyInr} accent="#059669" testId="kpi-revenue" />
      <KpiTile label="Overall ROI" value={`${dash.overall_roi}%`} icon={ChartBar} testId="kpi-roi" />
    </div>
    <div className="grid md:grid-cols-2 gap-4">
      <div className="bg-white p-4 rounded border border-slate-200">
        <h3 className="font-bold text-slate-900 mb-3">🏆 Top Performing</h3>
        {dash.top_campaigns.length === 0 && <p className="text-sm text-slate-500">No data yet</p>}
        {dash.top_campaigns.map(c => (
          <button key={c.campaign_id} onClick={() => onOpenCampaign(c.campaign_id)} className="w-full text-left flex items-center justify-between py-2 border-b border-slate-100 hover:bg-slate-50 px-2 rounded">
            <div>
              <p className="text-sm font-semibold text-slate-900">{c.name}</p>
              <p className="text-xs text-slate-500">{c.campaign_code} · {c.lead_count} leads · {fmtINR(c.spend)} spent</p>
            </div>
            <span className="text-sm font-bold text-emerald-600">ROI {c.roi}%</span>
          </button>
        ))}
      </div>
      <div className="bg-white p-4 rounded border border-slate-200">
        <h3 className="font-bold text-slate-900 mb-3">📉 Underperforming</h3>
        {dash.underperforming.length === 0 && <p className="text-sm text-slate-500">—</p>}
        {dash.underperforming.map(c => (
          <button key={c.campaign_id} onClick={() => onOpenCampaign(c.campaign_id)} className="w-full text-left flex items-center justify-between py-2 border-b border-slate-100 hover:bg-slate-50 px-2 rounded">
            <div>
              <p className="text-sm font-semibold text-slate-900">{c.name}</p>
              <p className="text-xs text-slate-500">{c.campaign_code} · {c.lead_count} leads</p>
            </div>
            <span className="text-sm font-bold text-red-600">ROI {c.roi}%</span>
          </button>
        ))}
      </div>
    </div>
    {alerts && (
      <div className="bg-amber-50 border border-amber-200 p-4 rounded">
        <h3 className="font-bold text-amber-900 mb-2 flex items-center gap-2"><Warning size={18} weight="fill" /> Alerts</h3>
        <ul className="text-sm text-amber-900 space-y-1">
          <li>Starting soon: <b>{alerts.starting_soon.length}</b></li>
          <li>Ending soon: <b>{alerts.ending_soon.length}</b></li>
          <li>Over budget: <b>{alerts.over_budget.length}</b></li>
          <li>Low response: <b>{alerts.low_response.length}</b></li>
          <li>Pending approvals: <b>{alerts.pending_approvals.length}</b></li>
          <li>Stale leads (&gt;7d): <b>{alerts.stale_leads}</b></li>
        </ul>
      </div>
    )}
  </div>
);

/* ------------- Campaigns List ------------- */
const CampaignsList = ({ campaigns, role, canDelete, onOpen, onEdit, onSubmit, onDelete, onSetStatus }) => {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const filtered = campaigns.filter(c => {
    if (filter !== "all" && c.status !== filter) return false;
    if (search && !(c.name?.toLowerCase().includes(search.toLowerCase()) || c.campaign_code?.toLowerCase().includes(search.toLowerCase()))) return false;
    return true;
  });
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <input data-testid="mktg-search" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded text-sm" />
        <select data-testid="mktg-status-filter" value={filter} onChange={e => setFilter(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded text-sm">
          <option value="all">All Statuses</option>
          {["draft", "submitted", "manager_approved", "marketing_head_approved", "finance_approved", "active", "on_hold", "completed", "cancelled", "closed"].map(s => (
            <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
          ))}
        </select>
        <span className="text-xs text-slate-500 ml-auto">{filtered.length} campaign{filtered.length !== 1 && "s"}</span>
      </div>
      <div className="bg-white rounded border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-3">Code</th>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Owner</th>
              <th className="text-right p-3">Budget</th>
              <th className="text-right p-3">Leads</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="8" className="p-6 text-center text-slate-500">No campaigns yet</td></tr>}
            {filtered.map(c => (
              <tr key={c.campaign_id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-3 font-mono text-xs">{c.campaign_code}</td>
                <td className="p-3">
                  <button data-testid={`mktg-open-${c.campaign_code}`} onClick={() => onOpen(c.campaign_id)} className="font-semibold text-[#002FA7] hover:underline text-left">{c.name}</button>
                  <p className="text-xs text-slate-500">{c.objective}</p>
                </td>
                <td className="p-3 capitalize text-xs text-slate-600">{(c.type || "").replace(/_/g, " ")}</td>
                <td className="p-3 text-xs text-slate-600">{c.owner_name || "—"}</td>
                <td className="p-3 text-right text-xs">
                  <div>{fmtINR(c.budget_approved || c.budget_planned)}</div>
                  <div className="text-slate-500">spent {fmtINR(c.budget_spent)}</div>
                </td>
                <td className="p-3 text-right text-xs">
                  <div className="font-bold">{c.actual_leads || 0}</div>
                  <div className="text-slate-500">exp {c.expected_leads || 0}</div>
                </td>
                <td className="p-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${getStatusBadgeClass(c.status)}`}>{(c.status || "").replace(/_/g, " ")}</span>
                </td>
                <td className="p-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {c.status === "draft" && (
                      <button data-testid={`mktg-submit-${c.campaign_code}`} onClick={() => onSubmit(c)} className="p-1.5 text-[#002FA7] hover:bg-blue-50 rounded" title="Submit for approval">
                        <PaperPlaneTilt size={16} />
                      </button>
                    )}
                    <button onClick={() => onEdit(c)} className="p-1.5 text-slate-600 hover:bg-slate-100 rounded" title="Edit">
                      <PencilSimple size={16} />
                    </button>
                    {c.status === "active" && (
                      <select onChange={e => { if (e.target.value) onSetStatus(c, e.target.value); e.target.value = ""; }} className="text-xs border border-slate-300 rounded px-1 py-0.5" defaultValue="">
                        <option value="">⋯</option>
                        <option value="on_hold">On hold</option>
                        <option value="completed">Complete</option>
                        <option value="closed">Close</option>
                      </select>
                    )}
                    {canDelete && (
                      <button onClick={() => onDelete(c)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete">
                        <Trash size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ------------- Approvals List ------------- */
const ApprovalsList = ({ campaigns, role, canApprove, onOpen, onApprove, onReject }) => (
  <div className="space-y-3" data-testid="mktg-approvals-list">
    {campaigns.length === 0 && <p className="text-slate-500">No campaigns awaiting approval.</p>}
    {campaigns.map(c => (
      <div key={c.campaign_id} className="bg-white p-4 rounded border border-slate-200 flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <button onClick={() => onOpen(c.campaign_id)} className="font-bold text-slate-900 hover:underline">{c.name}</button>
            <span className="text-xs font-mono text-slate-500">{c.campaign_code}</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${getStatusBadgeClass(c.status)}`}>{c.status.replace(/_/g, " ")}</span>
          </div>
          <p className="text-xs text-slate-500">
            Owner: {c.owner_name || "—"} · Budget: {fmtINR(c.budget_planned)} · Expected leads: {c.expected_leads || 0}
          </p>
        </div>
        {canApprove && (
          <div className="flex gap-2">
            <button data-testid={`mktg-approve-${c.campaign_code}`} onClick={() => onApprove(c)}
              className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700 flex items-center gap-1">
              <CheckCircle size={14} weight="bold" /> Approve
            </button>
            <button onClick={() => onReject(c)}
              className="px-3 py-1.5 bg-red-600 text-white text-xs rounded hover:bg-red-700 flex items-center gap-1">
              <XCircle size={14} weight="bold" /> Reject
            </button>
          </div>
        )}
      </div>
    ))}
  </div>
);

/* ------------- Leads Table ------------- */
const LeadsTable = ({ leads, users, reload, stages }) => {
  const [stageEdit, setStageEdit] = useState({});
  const updateLead = async (lead, patch) => {
    try { await axios.put(`${API}/mktg-campaign-leads/${lead.lead_id}`, patch); toast.success("Updated"); reload(); }
    catch { toast.error("Update failed"); }
  };
  return (
    <div className="bg-white rounded border border-slate-200 overflow-hidden" data-testid="mktg-leads-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left p-3">Lead</th>
            <th className="text-left p-3">Campaign</th>
            <th className="text-left p-3">Channel</th>
            <th className="text-left p-3">Assigned</th>
            <th className="text-left p-3">Stage</th>
            <th className="text-left p-3">Captured</th>
          </tr>
        </thead>
        <tbody>
          {leads.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-slate-500">No leads yet</td></tr>}
          {leads.map(l => (
            <tr key={l.lead_id} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="p-3">
                <p className="font-semibold">{l.name}</p>
                <p className="text-xs text-slate-500">{l.email || l.phone}</p>
                {l.duplicate_of && <span className="text-[10px] font-bold text-red-500">DUPLICATE</span>}
              </td>
              <td className="p-3 text-xs font-mono">{l.campaign_code}</td>
              <td className="p-3 text-xs text-slate-600">{l.channel || "—"}</td>
              <td className="p-3">
                <select value={l.assigned_to_id || ""}
                  onChange={e => {
                    const u = users.find(u => u.user_id === e.target.value);
                    updateLead(l, { assigned_to_id: e.target.value, assigned_to_name: u?.name || "" });
                  }}
                  className="text-xs border border-slate-300 rounded px-1 py-1">
                  <option value="">— unassigned —</option>
                  {users.map(u => <option key={u.user_id} value={u.user_id}>{`${u.name} (${u.role})`}</option>)}
                </select>
              </td>
              <td className="p-3">
                <select value={stageEdit[l.lead_id] || l.stage}
                  onChange={e => { setStageEdit(s => ({ ...s, [l.lead_id]: e.target.value })); updateLead(l, { stage: e.target.value }); }}
                  className="text-xs border border-slate-300 rounded px-1 py-1">
                  {(stages || []).map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
              </td>
              <td className="p-3 text-xs text-slate-500">{(l.created_at || "").slice(0, 10)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ------------- Reports ------------- */
const REPORT_OPTIONS = [
  { key: "campaign_roi", label: "Campaign ROI" },
  { key: "campaign_lead", label: "Campaign-wise Leads" },
  { key: "campaign_conversion", label: "Conversion Rate" },
  { key: "budget_actual", label: "Budget vs Actual" },
  { key: "owner", label: "Owner-wise Performance" },
  { key: "region", label: "Region-wise Performance" },
  { key: "channel", label: "Channel-wise Leads" },
];

const ReportsView = ({ reportType, setReportType, rows }) => {
  const headers = rows.length ? Object.keys(rows[0]) : [];
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <select data-testid="mktg-report-type" value={reportType} onChange={e => setReportType(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded text-sm">
          {REPORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        <span className="text-xs text-slate-500">{rows.length} rows</span>
      </div>
      <div className="bg-white rounded border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wider">
            <tr>{headers.map(h => <th key={h} className="text-left p-3">{h.replace(/_/g, " ")}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={headers.length || 1} className="p-6 text-center text-slate-500">No data</td></tr>}
            {rows.map((r, i) => {
              const rowKey = r.campaign_id || r.campaign_code || r.id || `${r.channel || r.region || r.owner || "row"}-${i}`;
              return (
                <tr key={rowKey} className="border-t border-slate-100">
                  {headers.map(h => <td key={h} className="p-3 text-xs">{typeof r[h] === "number" ? r[h].toLocaleString("en-IN") : r[h]}</td>)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ------------- Alerts ------------- */
const AlertBlock = ({ title, items, fmt }) => (
  <div className="bg-white rounded border border-slate-200 p-4">
    <h3 className="font-bold text-slate-900 mb-2">{title} <span className="text-sm font-normal text-slate-500">({items.length})</span></h3>
    {items.length === 0 ? <p className="text-xs text-slate-500">None</p> : (
      <ul className="text-xs space-y-1">
        {items.map((it, i) => {
          const k = it.campaign_code || it.id || it.campaign_id || `alert-${i}`;
          return <li key={k} className="flex justify-between">{fmt(it)}</li>;
        })}
      </ul>
    )}
  </div>
);

const AlertsView = ({ alerts }) => (
  <div className="grid md:grid-cols-2 gap-3">
    <AlertBlock title="⏳ Starting soon (3 days)" items={alerts.starting_soon}
      fmt={it => <><span>{it.campaign_code} — {it.name}</span><span className="text-slate-500">{it.start_date}</span></>} />
    <AlertBlock title="⌛ Ending soon" items={alerts.ending_soon}
      fmt={it => <><span>{it.campaign_code} — {it.name}</span><span className="text-slate-500">{it.end_date}</span></>} />
    <AlertBlock title="💸 Over budget" items={alerts.over_budget}
      fmt={it => <><span>{it.campaign_code} — {it.name}</span><span className="text-red-600">{fmtINR(it.excess)} over</span></>} />
    <AlertBlock title="📉 Low response" items={alerts.low_response}
      fmt={it => <><span>{it.campaign_code} — {it.name}</span><span className="text-slate-500">{it.actual}/{it.expected}</span></>} />
    <AlertBlock title="🕒 Pending approvals" items={alerts.pending_approvals}
      fmt={it => <><span>{it.campaign_code} — {it.name}</span><span className="text-slate-500">{it.status.replace(/_/g, " ")}</span></>} />
    <div className="bg-white rounded border border-slate-200 p-4">
      <h3 className="font-bold text-slate-900 mb-2">🧊 Stale leads</h3>
      <p className="text-3xl font-bold text-amber-600">{alerts.stale_leads}</p>
      <p className="text-xs text-slate-500">New/contacted, not updated in 7+ days</p>
    </div>
  </div>
);

/* ------------- Create / Edit Modal ------------- */
const CampaignEditorModal = ({ form, setForm, editing, config, users, onClose, onSave, toggleChannel }) => (
  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40 p-4">
    <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mktg-campaign-editor">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <h2 className="text-lg font-bold">{editing ? `Edit Campaign (${editing.campaign_code})` : "New Campaign"}</h2>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-800 text-xl leading-none">×</button>
      </div>
      <div className="p-5 grid md:grid-cols-2 gap-4 text-sm">
        <Field label="Campaign Name *" col2>
          <input data-testid="fld-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-300 rounded" />
        </Field>
        <Field label="Type">
          <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-300 rounded capitalize">
            {(config.types || []).map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
        </Field>
        <Field label="Objective">
          <input value={form.objective} onChange={e => setForm(f => ({ ...f, objective: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-300 rounded" />
        </Field>
        <Field label="Product / Service">
          <input value={form.product} onChange={e => setForm(f => ({ ...f, product: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-300 rounded" />
        </Field>
        <Field label="Owner">
          <select value={form.owner_id || ""} onChange={e => { const u = users.find(u => u.user_id === e.target.value); setForm(f => ({ ...f, owner_id: e.target.value, owner_name: u?.name || "" })); }}
            className="w-full px-3 py-2 border border-slate-300 rounded">
            <option value="">— self —</option>
            {users.map(u => <option key={u.user_id} value={u.user_id}>{`${u.name} (${u.role})`}</option>)}
          </select>
        </Field>
        <Field label="Department / Branch">
          <input value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-300 rounded" />
        </Field>
        <Field label="Region">
          <input value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-300 rounded" />
        </Field>
        <Field label="Start Date">
          <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-300 rounded" />
        </Field>
        <Field label="End Date">
          <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-300 rounded" />
        </Field>
        <Field label="Expected Leads">
          <input type="number" value={form.expected_leads} onChange={e => setForm(f => ({ ...f, expected_leads: Number(e.target.value) }))}
            className="w-full px-3 py-2 border border-slate-300 rounded" />
        </Field>
        <Field label="Expected Revenue (₹)">
          <input type="number" value={form.expected_revenue} onChange={e => setForm(f => ({ ...f, expected_revenue: Number(e.target.value) }))}
            className="w-full px-3 py-2 border border-slate-300 rounded" />
        </Field>
        <Field label="Budget Planned (₹)">
          <input type="number" value={form.budget_planned} onChange={e => setForm(f => ({ ...f, budget_planned: Number(e.target.value) }))}
            className="w-full px-3 py-2 border border-slate-300 rounded" />
        </Field>
        <Field label="Source Code / UTM">
          <input value={form.utm} onChange={e => setForm(f => ({ ...f, utm: e.target.value }))} placeholder="e.g. utm_source=meta_ads"
            className="w-full px-3 py-2 border border-slate-300 rounded" />
        </Field>
        <Field label="Channels" col2>
          <div className="flex flex-wrap gap-2">
            {(config.channels || []).map(ch => (
              <button key={ch} type="button" onClick={() => toggleChannel(ch)}
                className={`px-3 py-1 text-xs rounded border ${form.channels.includes(ch) ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-slate-600 border-slate-300"}`}>
                {ch.replace(/_/g, " ")}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Audience Segments (comma-separated)" col2>
          <input value={(form.audience_segments || []).join(", ")}
            onChange={e => setForm(f => ({ ...f, audience_segments: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }))}
            placeholder="e.g. SME Mumbai, High-value leads, Inactive customers"
            className="w-full px-3 py-2 border border-slate-300 rounded" />
        </Field>
        <Field label="Description" col2>
          <textarea rows="3" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-300 rounded" />
        </Field>
      </div>
      <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 border border-slate-300 rounded text-sm">Cancel</button>
        <button data-testid="mktg-save-campaign" onClick={onSave} className="px-4 py-2 bg-[#002FA7] text-white rounded text-sm">Save</button>
      </div>
    </div>
  </div>
);

const Field = ({ label, children, col2 }) => (
  <div className={col2 ? "md:col-span-2" : ""}>
    <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
    {children}
  </div>
);

/* ------------- Detail Drawer ------------- */
const CampaignDetailDrawer = ({ data, role, canApprove, canDelete, onClose, onSubmit, onApprove, onReject, onSetStatus, onDelete, onEdit, reload, user, config }) => {
  const [tab, setTab] = useState("summary");
  const c = data.campaign;
  return (
    <div className="fixed inset-0 bg-black/60 z-40 flex justify-end">
      <div className="w-full max-w-4xl bg-white h-full overflow-y-auto" data-testid="mktg-campaign-detail">
        <div className="p-4 border-b border-slate-200 flex items-start justify-between sticky top-0 bg-white z-10">
          <div>
            <p className="text-xs font-mono text-slate-500">{c.campaign_code}</p>
            <h2 className="text-xl font-bold">{c.name}</h2>
            <span className={`inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded ${getStatusBadgeClass(c.status)}`}>{(c.status || "").replace(/_/g, " ")}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => onEdit(c)} className="p-2 text-slate-600 hover:bg-slate-100 rounded"><PencilSimple size={18} /></button>
            {canDelete && <button onClick={() => { onDelete(c); onClose(); }} className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash size={18} /></button>}
            <button onClick={onClose} className="text-slate-500 hover:text-slate-800 text-xl leading-none px-2">×</button>
          </div>
        </div>

        {/* Workflow actions */}
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center gap-2 flex-wrap">
          {c.status === "draft" && <button onClick={() => onSubmit(c)} className="px-3 py-1.5 bg-[#002FA7] text-white text-xs rounded flex items-center gap-1"><PaperPlaneTilt size={14} /> Submit for approval</button>}
          {canApprove && ["submitted", "manager_approved", "marketing_head_approved"].includes(c.status) && (
            <>
              <button onClick={() => onApprove(c)} className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded flex items-center gap-1"><CheckCircle size={14} weight="bold" /> Approve next</button>
              <button onClick={() => onReject(c)} className="px-3 py-1.5 bg-red-600 text-white text-xs rounded flex items-center gap-1"><XCircle size={14} weight="bold" /> Reject</button>
            </>
          )}
          {c.status === "active" && ["admin", "marketing_head", "manager"].includes(role) && (
            <>
              <button onClick={() => onSetStatus(c, "on_hold")} className="px-3 py-1.5 bg-amber-600 text-white text-xs rounded">On Hold</button>
              <button onClick={() => onSetStatus(c, "completed")} className="px-3 py-1.5 bg-slate-600 text-white text-xs rounded">Complete</button>
              <button onClick={() => onSetStatus(c, "closed")} className="px-3 py-1.5 bg-slate-800 text-white text-xs rounded">Close</button>
            </>
          )}
          {(c.status === "on_hold" || c.status === "completed") && ["admin", "marketing_head", "manager"].includes(role) && (
            <button onClick={() => onSetStatus(c, "active")} className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded">Reactivate</button>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 px-4 flex gap-1 overflow-x-auto">
          {["summary", "leads", "comms", "docs", "spend", "approvals", "analytics", "capture"].map(t => (
            <button key={t} data-testid={`detail-tab-${t}`} onClick={() => setTab(t)}
              className={`px-3 py-2 text-xs font-semibold border-b-2 capitalize ${tab === t ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              {t}
            </button>
          ))}
        </div>

        <div className="p-5">
          {tab === "summary" && <SummaryTab c={c} metrics={data.metrics} />}
          {tab === "leads" && <DrawerLeads leads={data.leads} />}
          {tab === "comms" && <DrawerComms comms={data.comms} campaignId={c.campaign_id} user={user} config={config} reload={reload} />}
          {tab === "docs" && <DrawerDocs docs={data.docs} campaignId={c.campaign_id} user={user} reload={reload} />}
          {tab === "spend" && <DrawerSpend spends={data.spends} campaignId={c.campaign_id} user={user} reload={reload} />}
          {tab === "approvals" && <DrawerApprovals approvals={data.approvals} />}
          {tab === "analytics" && <DrawerAnalytics data={data} />}
          {tab === "capture" && <DrawerCapture campaignId={c.campaign_id} config={config} reload={reload} />}
        </div>
      </div>
    </div>
  );
};

const SummaryTab = ({ c, metrics }) => (
  <div className="grid md:grid-cols-2 gap-4 text-sm">
    <Info label="Objective" value={c.objective || "—"} />
    <Info label="Type" value={(c.type || "").replace(/_/g, " ")} />
    <Info label="Product" value={c.product || "—"} />
    <Info label="Region / Department" value={`${c.region || "—"} / ${c.department || "—"}`} />
    <Info label="Owner" value={c.owner_name || "—"} />
    <Info label="Start / End" value={`${c.start_date || "—"} → ${c.end_date || "—"}`} />
    <Info label="Expected leads / rev" value={`${c.expected_leads || 0} · ${fmtINR(c.expected_revenue)}`} />
    <Info label="Budget (P/A/Spent)" value={`${fmtINR(c.budget_planned)} / ${fmtINR(c.budget_approved)} / ${fmtINR(c.budget_spent)}`} />
    <Info label="UTM / Source" value={c.utm || "—"} />
    <Info label="Channels" value={(c.channels || []).join(", ") || "—"} />
    <div className="md:col-span-2 border-t border-slate-200 pt-3 mt-2 grid grid-cols-2 md:grid-cols-4 gap-3">
      <Info label="Leads" value={metrics.lead_count} />
      <Info label="MQL / SQL" value={`${metrics.mql} / ${metrics.sql}`} />
      <Info label="Conv rate" value={`${metrics.conversion_rate}%`} />
      <Info label="ROI" value={`${metrics.roi}%`} />
      <Info label="CPL" value={fmtINR(metrics.cpl)} />
      <Info label="CP-MQL" value={fmtINR(metrics.cp_mql)} />
      <Info label="Revenue" value={fmtINR(metrics.revenue_closed)} />
      <Info label="Opportunities" value={metrics.opportunity} />
    </div>
    <div className="md:col-span-2">
      <Info label="Description" value={c.description || "—"} />
    </div>
  </div>
);

const Info = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase font-bold text-slate-500 tracking-wider">{label}</p>
    <p className="text-sm text-slate-900">{value}</p>
  </div>
);

const DrawerLeads = ({ leads }) => (
  <div className="space-y-2">
    {leads.length === 0 && <p className="text-sm text-slate-500">No leads captured yet.</p>}
    {leads.map(l => (
      <div key={l.lead_id} className="border border-slate-200 rounded p-3 flex items-center justify-between">
        <div>
          <p className="font-semibold text-sm">{l.name}</p>
          <p className="text-xs text-slate-500">{l.email || l.phone} · {l.channel || "—"}</p>
        </div>
        <div className="text-right">
          <StageBadge stage={l.stage} />
          <p className="text-xs text-slate-500 mt-1">{l.assigned_to_name || "Unassigned"}</p>
        </div>
      </div>
    ))}
  </div>
);

const DrawerComms = ({ comms, campaignId, user, config, reload }) => {
  const [f, setF] = useState({ channel: "email", direction: "outbound", subject: "", body: "", outcome: "" });
  const submit = async () => {
    if (!f.body) { toast.error("Body required"); return; }
    try {
      await axios.post(`${API}/mktg-campaigns/${campaignId}/comms`, {
        ...f, by_user_id: user.user_id, by_user_name: user.name,
      });
      toast.success("Logged");
      setF({ channel: "email", direction: "outbound", subject: "", body: "", outcome: "" });
      reload();
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="space-y-3">
      <div className="bg-slate-50 p-3 rounded border border-slate-200">
        <p className="text-xs font-bold text-slate-600 mb-2">Log communication</p>
        <div className="grid md:grid-cols-3 gap-2 mb-2">
          <select value={f.channel} onChange={e => setF({ ...f, channel: e.target.value })} className="px-2 py-1 border border-slate-300 rounded text-xs">
            {(config.channels || []).map(c => <option key={c}>{c}</option>)}
          </select>
          <select value={f.direction} onChange={e => setF({ ...f, direction: e.target.value })} className="px-2 py-1 border border-slate-300 rounded text-xs">
            <option value="outbound">Outbound</option><option value="inbound">Inbound</option>
          </select>
          <input placeholder="Outcome (e.g. interested)" value={f.outcome} onChange={e => setF({ ...f, outcome: e.target.value })} className="px-2 py-1 border border-slate-300 rounded text-xs" />
        </div>
        <input placeholder="Subject" value={f.subject} onChange={e => setF({ ...f, subject: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-xs mb-1" />
        <textarea rows="2" placeholder="Notes / body" value={f.body} onChange={e => setF({ ...f, body: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-xs mb-2" />
        <button onClick={submit} className="px-3 py-1.5 bg-[#002FA7] text-white text-xs rounded">Add log</button>
      </div>
      {comms.length === 0 && <p className="text-sm text-slate-500">No communication logged.</p>}
      {comms.map(com => (
        <div key={com.comm_id} className="border border-slate-200 rounded p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-700 capitalize">{com.channel} · {com.direction}</span>
            <span className="text-xs text-slate-500">{(com.created_at || "").slice(0, 10)}</span>
          </div>
          {com.subject && <p className="text-sm font-semibold mt-1">{com.subject}</p>}
          <p className="text-sm text-slate-600">{com.body}</p>
          {com.outcome && <p className="text-xs text-emerald-700 mt-1">Outcome: {com.outcome}</p>}
          <p className="text-xs text-slate-500 mt-1">by {com.by_user_name}</p>
        </div>
      ))}
    </div>
  );
};

const DrawerDocs = ({ docs, campaignId, user, reload }) => {
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("File > 5 MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setUploading(true);
      try {
        await axios.post(`${API}/mktg-campaigns/${campaignId}/docs`, {
          name: f.name, size: f.size, data_url: reader.result,
          doc_type: "creative", uploaded_by: user.name,
        });
        toast.success("Uploaded"); reload();
      } catch { toast.error("Upload failed"); } finally { setUploading(false); e.target.value = ""; }
    };
    reader.readAsDataURL(f);
  };
  const del = async (id) => {
    if (!window.confirm("Delete document?")) return;
    try { await axios.delete(`${API}/mktg-campaigns/docs/${id}`); reload(); } catch { toast.error("Delete failed"); }
  };
  return (
    <div>
      <div className="mb-3">
        <label className="inline-flex items-center gap-2 px-3 py-2 bg-slate-900 text-white text-xs rounded cursor-pointer">
          <PaperclipHorizontal size={14} /> {uploading ? "Uploading…" : "Attach creative / brochure / proposal"}
          <input type="file" onChange={onFile} className="hidden" />
        </label>
      </div>
      {docs.length === 0 && <p className="text-sm text-slate-500">No attachments.</p>}
      {docs.map(d => (
        <div key={d.doc_id} className="border border-slate-200 rounded p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText size={20} className="text-slate-500" />
            <div>
              <p className="text-sm font-semibold">{d.name}</p>
              <p className="text-xs text-slate-500">{Math.round(d.size / 1024)} KB · {d.uploaded_by}</p>
            </div>
          </div>
          <button onClick={() => del(d.doc_id)} className="text-red-600 text-xs hover:underline">Remove</button>
        </div>
      ))}
    </div>
  );
};

const DrawerSpend = ({ spends, campaignId, user, reload }) => {
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("ad_spend");
  const [vendor, setVendor] = useState("");
  const add = async () => {
    if (!amount) return;
    try {
      await axios.post(`${API}/mktg-campaigns/${campaignId}/spend`, {
        amount: Number(amount), category, vendor, by_user_id: user.user_id,
      });
      setAmount(""); setVendor(""); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3">
      <div className="bg-slate-50 p-3 rounded border border-slate-200 grid md:grid-cols-4 gap-2">
        <input type="number" placeholder="Amount ₹" value={amount} onChange={e => setAmount(e.target.value)} className="px-2 py-1 border border-slate-300 rounded text-sm" />
        <select value={category} onChange={e => setCategory(e.target.value)} className="px-2 py-1 border border-slate-300 rounded text-sm">
          {["ad_spend", "vendor_cost", "event_expense", "operational", "other"].map(x => <option key={x}>{x}</option>)}
        </select>
        <input placeholder="Vendor (optional)" value={vendor} onChange={e => setVendor(e.target.value)} className="px-2 py-1 border border-slate-300 rounded text-sm" />
        <button onClick={add} className="px-3 py-1 bg-[#002FA7] text-white rounded text-sm">Log spend</button>
      </div>
      {spends.length === 0 && <p className="text-sm text-slate-500">No spend logged.</p>}
      {spends.map(s => (
        <div key={s.spend_id} className="border border-slate-200 rounded p-3 flex justify-between">
          <div><p className="text-sm font-semibold">{fmtINR(s.amount)}</p><p className="text-xs text-slate-500">{s.category} · {s.vendor || "—"}</p></div>
          <p className="text-xs text-slate-500">{(s.created_at || "").slice(0, 10)}</p>
        </div>
      ))}
    </div>
  );
};

const DrawerApprovals = ({ approvals }) => (
  <div className="space-y-2">
    {approvals.length === 0 && <p className="text-sm text-slate-500">No approval history yet.</p>}
    {approvals.map(a => (
      <div key={a.approval_id} className="border-l-4 border-[#002FA7] bg-slate-50 p-3 rounded">
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold capitalize">{a.action} → {a.stage.replace(/_/g, " ")}</span>
          <span className="text-xs text-slate-500">{(a.created_at || "").slice(0, 16).replace("T", " ")}</span>
        </div>
        <p className="text-xs text-slate-600">by {a.by_user_name} ({a.by_role})</p>
        {a.remarks && <p className="text-xs italic mt-1">"{a.remarks}"</p>}
      </div>
    ))}
  </div>
);

const DrawerAnalytics = ({ data }) => (
  <div className="grid md:grid-cols-3 gap-3">
    <div className="bg-slate-50 p-3 rounded">
      <p className="text-xs font-bold text-slate-600 mb-2">By stage</p>
      {data.by_stage.map(s => s.count > 0 && (
        <div key={s.stage} className="flex justify-between py-0.5 text-xs">
          <span className="capitalize">{s.stage.replace(/_/g, " ")}</span><span className="font-bold">{s.count}</span>
        </div>
      ))}
    </div>
    <div className="bg-slate-50 p-3 rounded">
      <p className="text-xs font-bold text-slate-600 mb-2">By channel</p>
      {data.by_channel.map(s => (
        <div key={s.channel} className="flex justify-between py-0.5 text-xs">
          <span className="capitalize">{s.channel.replace(/_/g, " ")}</span><span className="font-bold">{s.count}</span>
        </div>
      ))}
    </div>
    <div className="bg-slate-50 p-3 rounded">
      <p className="text-xs font-bold text-slate-600 mb-2">By owner</p>
      {data.by_owner.map(s => (
        <div key={s.owner} className="flex justify-between py-0.5 text-xs">
          <span>{s.owner}</span><span className="font-bold">{s.count}</span>
        </div>
      ))}
    </div>
  </div>
);

const DrawerCapture = ({ campaignId, config, reload }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", company: "", channel: "website_form", source: "manual", notes: "", city: "" });
  const submit = async () => {
    if (!f.name || (!f.email && !f.phone)) { toast.error("Name and (email or phone) required"); return; }
    try {
      const r = await axios.post(`${API}/mktg-campaigns/${campaignId}/capture`, f);
      if (r.data.duplicate_of) toast.warning("Duplicate detected — flagged");
      else toast.success("Lead captured");
      setF({ name: "", email: "", phone: "", company: "", channel: "website_form", source: "manual", notes: "", city: "" });
      reload();
    } catch { toast.error("Capture failed"); }
  };
  return (
    <div className="bg-slate-50 p-4 rounded border border-slate-200">
      <p className="text-sm font-bold text-slate-700 mb-3">Manually capture a response / lead</p>
      <div className="grid md:grid-cols-2 gap-2 text-sm">
        <input placeholder="Name *" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="px-2 py-1.5 border border-slate-300 rounded" />
        <input placeholder="Phone" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} className="px-2 py-1.5 border border-slate-300 rounded" />
        <input placeholder="Email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} className="px-2 py-1.5 border border-slate-300 rounded" />
        <input placeholder="Company" value={f.company} onChange={e => setF({ ...f, company: e.target.value })} className="px-2 py-1.5 border border-slate-300 rounded" />
        <input placeholder="City" value={f.city} onChange={e => setF({ ...f, city: e.target.value })} className="px-2 py-1.5 border border-slate-300 rounded" />
        <select value={f.channel} onChange={e => setF({ ...f, channel: e.target.value })} className="px-2 py-1.5 border border-slate-300 rounded">
          {(config.channels || []).map(c => <option key={c}>{c}</option>)}
        </select>
        <textarea placeholder="Notes" rows="2" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} className="md:col-span-2 px-2 py-1.5 border border-slate-300 rounded" />
      </div>
      <button data-testid="mktg-capture-submit" onClick={submit} className="mt-3 px-4 py-2 bg-[#002FA7] text-white text-sm rounded">Capture lead</button>
    </div>
  );
};

export default MarketingCampaigns;
